// ============================================================
// ChannelsDialog (v8.363.6) - the GM's vox groups.
//
// A group is a named list of PEOPLE, not a sheet team: players switch
// characters at will. "Seed from a team" reads the published actingAs
// once - whoever is acting as a member now joins - and says who was
// left out. "GM listens" is per group, on by default.
// ============================================================
import { useState } from 'react';
import common from '../styles/common.module.css';
import styles from '../styles/Permissions.module.css';
import clsx from '../utils/clsx';
import useFocusTrap from '../hooks/useFocusTrap.js';
import useModalBackdrop from '../hooks/useModalBackdrop';
import useEscapeKey from '../hooks/useEscapeKey.js';
import type { ChatChannel, Person } from '../engine/chat';
import { warnIfRejected } from '../diagnostics/log';

interface TeamForSeed {
  tag: string;
  label: string;
  members: { id: string }[];
}

interface Props {
  show: boolean;
  onClose: () => void;
  channels: ChatChannel[];
  people: Person[];
  teams: TeamForSeed[];
  actingBy: Record<string, string>;
  onCreate: (input: { name: string; members: string[]; gmListens: boolean }) => Promise<unknown>;
  onUpdate: (id: string, patch: Partial<Pick<ChatChannel, 'name' | 'members' | 'gmListens'>>) => Promise<unknown>;
  onRemove: (id: string) => Promise<unknown>;
  onSeed: (team: TeamForSeed, actingBy: Record<string, string>) => Promise<{ id: string | null; uids: string[] }>;
}

const ROW_LABEL = { flexDirection: 'row', gap: 4, alignItems: 'center' } as const;

const personLabel = (p: Person) => (p.actingAsName ? `${p.name} (acting as ${p.actingAsName})` : p.name);
const withOrWithout = (list: string[], uid: string, on: boolean) =>
  on ? [...list, uid] : list.filter((u) => u !== uid);

/** What seeding said: who joined and how many combatants nobody acts as - or that the group is empty. */
function seedNote(team: TeamForSeed, uids: string[], nameOf: (uid: string) => string): string {
  if (!uids.length) return `${team.label}: nobody is acting as one of its combatants yet - an empty group was made.`;
  const missing = team.members.length - uids.length;
  const gap = missing > 0 ? ` - ${missing} of its combatants have nobody acting as them` : '';
  return `${team.label}: ${uids.map(nameOf).join(', ')}${gap}.`;
}

/**
 * The dialog's drafts - the new group and the team to seed from - kept
 * while it is closed, as they always were, so reopening finds them.
 */
function useDrafts() {
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [gmListens, setGmListens] = useState(true);
  const [seedTag, setSeedTag] = useState('');
  const [note, setNote] = useState<string | null>(null);
  return { name, setName, picked, setPicked, gmListens, setGmListens, seedTag, setSeedTag, note, setNote };
}

type Drafts = ReturnType<typeof useDrafts>;

/** An existing group: its name, its members, whether the GM listens, and Delete. */
function ChannelCard({
  ch,
  players,
  onUpdate,
  onRemove,
}: { ch: ChatChannel; players: Person[] } & Pick<Props, 'onUpdate' | 'onRemove'>) {
  return (
    <div className={styles.playerCard} data-testid={`channel-${ch.id}`}>
      <input
        className={common.input}
        value={ch.name}
        aria-label={`Name of ${ch.name}`}
        onChange={(e) => warnIfRejected(onUpdate(ch.id, { name: e.target.value }), 'chat', 'group rename')}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '6px 0' }}>
        {players.map((p) => (
          <label key={p.uid} className={common.label} style={ROW_LABEL}>
            <input
              type="checkbox"
              checked={ch.members.includes(p.uid)}
              aria-label={`${p.name} in ${ch.name}`}
              onChange={(e) =>
                warnIfRejected(
                  onUpdate(ch.id, { members: withOrWithout(ch.members, p.uid, e.target.checked) }),
                  'chat',
                  'group membership change',
                )
              }
            />
            {personLabel(p)}
          </label>
        ))}
        {players.length === 0 && <span className={styles.hint}>No players seated.</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <label className={common.label} style={{ ...ROW_LABEL, margin: 0 }}>
          <input
            type="checkbox"
            checked={ch.gmListens}
            aria-label={`GM listens to ${ch.name}`}
            onChange={(e) =>
              warnIfRejected(onUpdate(ch.id, { gmListens: e.target.checked }), 'chat', 'group listening change')
            }
          />
          GM listens
        </label>
        <button
          className={clsx(common.btnSmall, styles.mlAuto)}
          onClick={() => warnIfRejected(onRemove(ch.id), 'chat', 'group delete')}
          data-testid={`channel-remove-${ch.id}`}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function NewGroupForm({ d, players, onCreate }: { d: Drafts; players: Person[]; onCreate: () => void }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        className={common.input}
        value={d.name}
        placeholder="Kill-team Ares"
        aria-label="New group name"
        data-testid="channel-new-name"
        onChange={(e) => d.setName(e.target.value)}
      />
      {players.map((p) => (
        <label key={p.uid} className={common.label} style={ROW_LABEL}>
          <input
            type="checkbox"
            checked={d.picked.includes(p.uid)}
            aria-label={`${p.name} in the new group`}
            onChange={(e) => d.setPicked((s) => withOrWithout(s, p.uid, e.target.checked))}
          />
          {personLabel(p)}
        </label>
      ))}
      <label className={common.label} style={ROW_LABEL}>
        <input
          type="checkbox"
          checked={d.gmListens}
          aria-label="GM listens to the new group"
          onChange={(e) => d.setGmListens(e.target.checked)}
        />
        GM listens
      </label>
      <button className={common.btnSmall} onClick={onCreate} data-testid="channel-create" disabled={!d.name.trim()}>
        Create
      </button>
    </div>
  );
}

function SeedForm({ d, teams, onSeed }: { d: Drafts; teams: TeamForSeed[]; onSeed: () => void }) {
  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select
          className={common.input}
          value={d.seedTag}
          aria-label="Team to seed from"
          data-testid="channel-seed-team"
          onChange={(e) => d.setSeedTag(e.target.value)}
        >
          <option value="">Pick a team…</option>
          {teams.map((t) => (
            <option key={t.tag} value={t.tag}>
              {t.label}
            </option>
          ))}
        </select>
        <button className={common.btnSmall} onClick={onSeed} data-testid="channel-seed" disabled={!d.seedTag}>
          Make a group of whoever acts as them
        </button>
      </div>
      {d.note && (
        <p className={styles.hint} data-testid="channel-seed-note">
          {d.note}
        </p>
      )}
    </>
  );
}

export default function ChannelsDialog(props: Props) {
  const { show, onClose, channels, people, teams } = props;
  const trapRef = useFocusTrap(show);
  const backdrop = useModalBackdrop(onClose, trapRef);
  useEscapeKey(show, onClose);
  const d = useDrafts();
  if (!show) return null;
  const players = people.filter((p) => !p.isGm);
  const nameOf = (uid: string) => people.find((p) => p.uid === uid)?.name ?? uid;

  const create = async () => {
    if (!d.name.trim()) return;
    await props.onCreate({ name: d.name.trim(), members: d.picked, gmListens: d.gmListens });
    d.setName('');
    d.setPicked([]);
    d.setGmListens(true);
    d.setNote(null);
  };
  const seed = async () => {
    const team = teams.find((t) => t.tag === d.seedTag);
    if (!team) return;
    const { uids } = await props.onSeed(team, props.actingBy);
    d.setNote(seedNote(team, uids, nameOf));
  };

  return (
    <div className={common.modal} onClick={backdrop.onBackdropClick} role="presentation">
      <div
        ref={backdrop.boxRef}
        className={clsx(common.modalContent, styles.content)}
        role="dialog"
        aria-modal="true"
        aria-label="Vox groups"
        data-testid="channels-dialog"
      >
        <h3 style={{ marginTop: 0 }}>Vox groups</h3>
        <p className={styles.hint}>A group is a list of people. Whoever they act as, the channel follows them.</p>
        {channels.length === 0 && <div className={common.overviewEmpty}>No groups yet.</div>}
        {channels.map((ch) => (
          <ChannelCard key={ch.id} ch={ch} players={players} onUpdate={props.onUpdate} onRemove={props.onRemove} />
        ))}
        <h4>New group</h4>
        <NewGroupForm d={d} players={players} onCreate={() => warnIfRejected(create(), 'chat', 'group create')} />
        <h4>Seed from a team</h4>
        <SeedForm d={d} teams={teams} onSeed={() => warnIfRejected(seed(), 'chat', 'group seed from a team')} />
        <div className={styles.footer}>
          <button className={common.btnSecondary} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
